import React from "react";
import { Link } from "react-router-dom";
import { Mail, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import MarstickLogoMark from "@/components/brand/MarstickLogoMark";
import { getLocalizedPath, getRouteKeyFromPath } from "@/lib/i18nRoutes";
import { useTranslation } from "@/i18n";
import { getNavLabel } from "@/config/navigation";
import { footerLinkGroups } from "@/lib/mainNavigation";

const legalGroups = [
  {
    key: "company",
    links: [
      { key: "about", label: "About", href: "/about" },
      { key: "contact", label: "Contact", href: "/contact" },
    ],
  },
  {
    key: "policies",
    links: [
      { key: "privacy", label: "Privacy Policy", href: "/privacy" },
      { key: "cookies", label: "Cookie Policy", href: "/cookies" },
      { key: "terms", label: "Terms & Conditions", href: "/terms" },
      { key: "advertisingPolicy", label: "Advertising Policy", href: "/advertising-policy" },
      { key: "editorialPolicy", label: "Editorial Policy", href: "/editorial-policy" },
    ],
  },
  {
    key: "safety",
    links: [
      { key: "disclaimer", label: "Disclaimer", href: "/disclaimer" },
      { key: "safetyDisclaimer", label: "Safety Disclaimer", href: "/safety-disclaimer" },
    ],
  },
];

const socialLinks = ["𝕏", "f", "◎", "in"];

const legalGroupLabels = {
  en: { company: "Company", policies: "Policies", safety: "Safety" },
  he: { company: "חברה", policies: "מדיניות", safety: "בטיחות" },
  es: { company: "Empresa", policies: "Políticas", safety: "Seguridad" },
  it: { company: "Azienda", policies: "Policy", safety: "Sicurezza" },
  fr: { company: "Entreprise", policies: "Politiques", safety: "Sécurité" },
  de: { company: "Unternehmen", policies: "Richtlinien", safety: "Sicherheit" },
  pt: { company: "Empresa", policies: "Políticas", safety: "Segurança" },
  ar: { company: "الشركة", policies: "السياسات", safety: "السلامة" },
  el: { company: "Εταιρεία", policies: "Πολιτικές", safety: "Ασφάλεια" },
  ru: { company: "Компания", policies: "Политики", safety: "Безопасность" },
  tr: { company: "Şirket", policies: "Politikalar", safety: "Güvenlik" },
  zh: { company: "公司", policies: "政策", safety: "安全" },
};

const privacyChoiceLabels = {
  en: "Cookie Settings",
  he: "בחירות פרטיות",
  es: "Opciones de privacidad",
  it: "Scelte privacy",
  fr: "Choix de confidentialité",
  de: "Datenschutzauswahl",
  pt: "Opções de privacidade",
  ar: "خيارات الخصوصية",
  el: "Επιλογές απορρήτου",
  ru: "Настройки конфиденциальности",
  tr: "Gizlilik tercihleri",
  zh: "隐私选择",
};

const trustCardCopy = {
  en: { title: "Trust, editorial standards, and policies", text: "Clear ownership, editorial review, privacy, and advertising policies are collected here in the footer for visitors and AdSense reviewers.", editorial: "Editorial Policy", advertising: "Advertising Policy", privacy: "Privacy" },
  he: { title: "אמון, סטנדרטים עריכתיים ומדיניות", text: "בעלות ברורה, סקירה עריכתית, פרטיות ומדיניות פרסום מרוכזות כאן בפוטר עבור מבקרים ובודקי AdSense.", editorial: "מדיניות עריכה", advertising: "מדיניות פרסום", privacy: "פרטיות" },
  es: { title: "Confianza, estándares editoriales y políticas", text: "La propiedad, la revisión editorial, la privacidad y las políticas publicitarias están claras antes de que visitantes o revisores de AdSense lleguen al pie de página.", editorial: "Política editorial", advertising: "Política publicitaria", privacy: "Privacidad" },
  it: { title: "Fiducia, standard editoriali e policy", text: "Proprietà, revisione editoriale, privacy e policy pubblicitarie sono disponibili prima che visitatori o revisori AdSense arrivino al footer.", editorial: "Politica editoriale", advertising: "Politica pubblicitaria", privacy: "Privacy" },
  fr: { title: "Confiance, standards éditoriaux et politiques", text: "Propriété, revue éditoriale, confidentialité et politiques publicitaires sont disponibles avant le pied de page pour les visiteurs et les examinateurs AdSense.", editorial: "Politique éditoriale", advertising: "Politique publicitaire", privacy: "Confidentialité" },
  de: { title: "Vertrauen, Redaktionsstandards und Richtlinien", text: "Klare Verantwortung, redaktionelle Prüfung, Datenschutz und Werberichtlinien sind vor dem Footer für Besucher und AdSense-Prüfer sichtbar.", editorial: "Redaktionsrichtlinie", advertising: "Werberichtlinie", privacy: "Datenschutz" },
  pt: { title: "Confiança, padrões editoriais e políticas", text: "Propriedade clara, revisão editorial, privacidade e políticas de publicidade aparecem antes do rodapé para visitantes e revisores do AdSense.", editorial: "Política editorial", advertising: "Política de publicidade", privacy: "Privacidade" },
  ar: { title: "الثقة والمعايير التحريرية والسياسات", text: "تظهر الملكية الواضحة والمراجعة التحريرية وسياسات الخصوصية والإعلانات قبل أن يصل الزوار أو مراجعو AdSense إلى التذييل.", editorial: "السياسة التحريرية", advertising: "سياسة الإعلانات", privacy: "الخصوصية" },
  el: { title: "Εμπιστοσύνη, συντακτικά πρότυπα και πολιτικές", text: "Σαφής ιδιοκτησία, συντακτικός έλεγχος, απόρρητο και πολιτικές διαφήμισης εμφανίζονται πριν το footer για επισκέπτες και ελεγκτές AdSense.", editorial: "Συντακτική πολιτική", advertising: "Πολιτική διαφήμισης", privacy: "Απόρρητο" },
  ru: { title: "Доверие, редакционные стандарты и политики", text: "Прозрачная ответственность, редакционная проверка, конфиденциальность и рекламные политики доступны до футера для посетителей и проверяющих AdSense.", editorial: "Редакционная политика", advertising: "Политика рекламы", privacy: "Конфиденциальность" },
  tr: { title: "Güven, editoryal standartlar ve politikalar", text: "Açık sahiplik, editoryal inceleme, gizlilik ve reklam politikaları ziyaretçiler ve AdSense inceleyicileri footer’a gelmeden önce görünür.", editorial: "Editoryal politika", advertising: "Reklam politikası", privacy: "Gizlilik" },
  zh: { title: "信任、编辑标准与政策", text: "清晰的所有权、编辑审核、隐私和广告政策会在页脚前展示，方便访客和 AdSense 审核者查看。", editorial: "编辑政策", advertising: "广告政策", privacy: "隐私" },
};

const withLang = (lang, href) => {
  const routeKey = getRouteKeyFromPath(href);
  return routeKey ? getLocalizedPath(routeKey, lang) : `/${lang}${href}`;
};

export default function PublicFooter({ lang = "en" }) {
  const { t } = useTranslation();
  const footer = t("public.footer", { defaultValue: {} });
  const trustCard = trustCardCopy[lang] || {};
  const sitemapLabel = {
    en: "Sitemap", he: "מפת אתר", es: "Mapa del sitio", it: "Mappa del sito", fr: "Plan du site", de: "Sitemap", pt: "Mapa do site", ar: "خريطة الموقع", el: "Χάρτης ιστότοπου", ru: "Карта сайта", tr: "Site haritası", zh: "网站地图",
  }[lang] || "";
  const fallbackFooter = {};
  const productLinks = footerLinkGroups.find((group) => group.category === "Product")?.links || [];
  const discoverLinks = footerLinkGroups.find((group) => group.category === "Discover")?.links || [];

  const titleize = (value = "") => String(value)
    .replace(/^\//, "")
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
  const cleanText = (value) => (typeof value === "string" ? value.trim() : value);
  const getLinkLabel = (item) => cleanText(footer.links?.[item.key]) || cleanText(fallbackFooter.links?.[item.key]) || cleanText(getNavLabel(item.key, lang)) || titleize(item.href || item.key) || "";
  const emailLabel = cleanText(footer.emailAria) || cleanText(footer.emailPlaceholder) || {
    en: "Email address", he: "כתובת אימייל", es: "Correo electrónico", it: "Indirizzo email", fr: "Adresse e-mail", de: "E-Mail-Adresse", pt: "Endereço de email", ar: "عنوان البريد الإلكتروني", el: "Διεύθυνση email", ru: "Адрес электронной почты", tr: "E-posta adresi", zh: "电子邮件地址",
  }[lang] || "Email address";

  return (
    <footer className="border-t border-border bg-card/70">
      <div className="maritime-container space-y-8 py-8 md:py-10">
        <section className="rounded-2xl border border-primary/20 bg-seafoam/70 p-5 shadow-sm md:flex md:items-center md:justify-between md:gap-6">
          <div>
            <p className="flex items-center gap-2 font-black text-ocean dark:text-white">
              <ShieldCheck className="h-4 w-4 text-primary" /> {trustCard.title}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">{trustCard.text}</p>
          </div>
          <div className="mt-4 flex flex-wrap gap-2 md:mt-0">
            <Button asChild variant="outline" className="rounded-full px-5"><Link to={withLang(lang, "/editorial-policy")}>{trustCard.editorial}</Link></Button>
            <Button asChild variant="outline" className="rounded-full px-5"><Link to={withLang(lang, "/advertising-policy")}>{trustCard.advertising}</Link></Button>
            <Button asChild className="rounded-full px-5"><Link to={withLang(lang, "/privacy")}>{trustCard.privacy}</Link></Button>
          </div>
        </section>

        <section className="grid gap-8 md:grid-cols-[1.15fr_0.9fr_0.9fr_1.35fr]">
          <div>
            <Link to={`/${lang}`} className="inline-flex items-center gap-2 font-heading text-lg font-black text-ocean dark:text-white">
              <MarstickLogoMark className="h-9 w-9 rounded-xl" />
              Marstick
            </Link>
            <p className="mt-4 max-w-xs text-sm leading-6 text-muted-foreground">{footer.description}</p>
            <div className="mt-4 flex gap-3">
              {socialLinks.map((item) => (
                <span key={item} className="grid h-9 w-9 place-items-center rounded-lg bg-sand text-sm font-black text-ocean shadow-sm dark:border dark:border-white/20 dark:bg-white/10 dark:text-white">{item}</span>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-black text-ocean dark:text-white">{footer.product}</h3>
            <div className="mt-4 space-y-3 text-sm">
              {productLinks.map((item) => {
                const label = getLinkLabel(item);
                return <Link key={item.key} to={withLang(lang, item.href)} aria-label={label} title={label} className="block text-muted-foreground hover:text-primary">{label}</Link>;
              })}
            </div>
          </div>

          <div>
            <h3 className="font-black text-ocean dark:text-white">{footer.discover}</h3>
            <div className="mt-4 space-y-3 text-sm">
              {discoverLinks.map((item) => {
                const label = getLinkLabel(item);
                return <Link key={item.key} to={withLang(lang, item.href)} aria-label={label} title={label} className="block text-muted-foreground hover:text-primary">{label}</Link>;
              })}
            </div>
          </div>

          <div className="rounded-2xl border border-border/70 bg-background/55 p-4 shadow-sm">
            <h3 className="font-black text-ocean dark:text-white">{footer.legal}</h3>
            <div className="mt-4 grid gap-4 text-sm sm:grid-cols-2 md:grid-cols-1 xl:grid-cols-2">
              {legalGroups.map((group) => (
                <div key={group.key} className="space-y-2">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-primary">{legalGroupLabels[lang]?.[group.key] || group.key}</p>
                  {group.links.map((item) => {
                    const label = getLinkLabel(item);
                    return <Link key={item.key} to={withLang(lang, item.href)} aria-label={label} title={label} className="block rounded-xl px-2 py-1.5 text-muted-foreground hover:bg-primary/10 hover:text-primary">{label}</Link>;
                  })}
                </div>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-2 border-t border-border/70 pt-4 text-sm font-bold">
              <a href="/sitemap.xml" className="rounded-full border border-border bg-card px-3 py-2 text-muted-foreground hover:border-primary/50 hover:text-primary">{sitemapLabel}</a>
              <button type="button" onClick={() => window.dispatchEvent(new Event("marstick:open-cookie-settings"))} className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-2 text-left text-muted-foreground hover:border-primary/50 hover:text-primary">
                <ShieldCheck className="h-4 w-4" /> {privacyChoiceLabels[lang] || ""}
              </button>
            </div>
          </div>
        </section>

        <section className="rounded-2xl bg-sand/70 p-5 md:flex md:items-center md:justify-between md:gap-8">
          <div>
            <h3 className="font-black text-ocean dark:text-white">{footer.stayUpdated}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{footer.stayUpdatedText}</p>
          </div>
          <form onSubmit={(event) => event.preventDefault()} className="mt-4 flex flex-col gap-3 sm:flex-row md:mt-0">
            <label htmlFor={`footer-email-${lang}`} className="sr-only">{emailLabel}</label>
            <input id={`footer-email-${lang}`} className="h-11 w-full min-w-0 rounded-xl border border-primary/20 bg-seafoam/70 px-4 text-sm outline-none focus:border-primary sm:w-auto sm:min-w-[16rem]" type="email" placeholder={cleanText(footer.emailPlaceholder) || emailLabel} aria-label={emailLabel} />
            <Button type="submit" className="rounded-xl"><Mail className="h-4 w-4" /> {footer.subscribe}</Button>
          </form>
        </section>
      </div>
    </footer>
  );
}